import React, { useEffect, useState } from 'react';
import api from '../../services/api';

const hoyISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const fecha = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
    return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
};

// Ventana "Recordar" de Pacientes: envía el recordatorio de control con los datos del paciente.
// Si el paciente no tenía correo o fecha de control, se completan aquí y quedan guardados en su ficha.
const ModalRecordatorio = ({ patient, onClose, onEnviado }) => {
    const [email, setEmail] = useState(patient.email || '');
    const [fechaControl, setFechaControl] = useState(patient.next_checkup_date || hoyISO());
    const [sucursal, setSucursal] = useState('ambas');
    const [preview, setPreview] = useState(null);
    const [anteriores, setAnteriores] = useState([]);
    const [enviando, setEnviando] = useState(false);
    const [errores, setErrores] = useState({});
    const [resultado, setResultado] = useState(null);

    useEffect(() => {
        api.get(`/api/patients/${patient.id}/recordatorios/`).then(r => setAnteriores(r.data || [])).catch(() => {});
    }, [patient.id]);

    useEffect(() => {
        if (!fechaControl) return;
        api.post('/api/patients/recordatorios/vista-previa/', { paciente_id: patient.id, fecha_control: fechaControl, sucursal })
            .then(r => setPreview(r.data))
            .catch(() => setPreview(null));
    }, [patient.id, fechaControl, sucursal]);

    const enviar = async () => {
        setEnviando(true);
        setErrores({});
        setResultado(null);
        try {
            const { data } = await api.post(`/api/patients/${patient.id}/enviar-recordatorio/`, { email, fecha_control: fechaControl, sucursal });
            setResultado({ ok: true, texto: data.detail });
            const r = await api.get(`/api/patients/${patient.id}/recordatorios/`);
            setAnteriores(r.data || []);
            if (onEnviado) onEnviado();
        } catch (e) {
            const err = e.response?.data || {};
            if (err.email || err.fecha_control) setErrores(err);
            else setResultado({ ok: false, texto: err.detail || 'No se pudo enviar el recordatorio.' });
        }
        setEnviando(false);
    };

    const vencido = fechaControl && fechaControl < hoyISO();

    return (
        <div className="mr-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <style>{`
                .mr-overlay { position: fixed; inset: 0; background: rgba(17,24,39,0.7); z-index: 10040; display: flex; align-items: center; justify-content: center; padding: 16px; }
                .mr-modal { background: #fff; border-radius: 16px; width: 980px; max-width: 100%; max-height: 92vh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 25px 50px rgba(0,0,0,0.3); }
                .mr-head { background: #111827; color: #fff; padding: 16px 22px; display: flex; align-items: center; justify-content: space-between; }
                .mr-body { display: grid; grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr); gap: 20px; padding: 20px 22px; overflow-y: auto; }
                .mr-field { margin-bottom: 14px; }
                .mr-field label { display: block; font-size: 0.8rem; font-weight: 700; color: #374151; margin-bottom: 4px; }
                .mr-field input { width: 100%; padding: 9px 11px; border: 1.5px solid #d1d5db; border-radius: 8px; font-size: 0.95rem; box-sizing: border-box; }
                .mr-field input.mr-err { border-color: #dc2626; background: #fef2f2; }
                .mr-msg-err { color: #dc2626; font-size: 0.8rem; margin-top: 3px; font-weight: 600; }
                .mr-hint { color: #6b7280; font-size: 0.78rem; margin-top: 3px; }
                .mr-chips { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 6px; }
                .mr-chips button { border: 1px solid #d1d5db; background: #fff; border-radius: 999px; padding: 2px 10px; font-size: 0.75rem; font-weight: 600; cursor: pointer; }
                .mr-frame { width: 100%; height: 430px; border: 1px solid #e5e7eb; border-radius: 10px; background: #f3f4f6; }
                .mr-btn { display: inline-flex; align-items: center; gap: 6px; padding: 10px 18px; border-radius: 8px; font-weight: 700; font-size: 0.92rem; cursor: pointer; border: none; background: #111827; color: #fff; width: 100%; justify-content: center; }
                .mr-btn:disabled { opacity: 0.6; cursor: default; }
                .mr-tabla { width: 100%; border-collapse: collapse; font-size: 0.8rem; }
                .mr-tabla td { padding: 5px 4px; border-bottom: 1px solid #f3f4f6; }
                @media (max-width: 820px) { .mr-body { grid-template-columns: 1fr; } }
            `}</style>
            <div className="mr-modal">
                <div className="mr-head">
                    <div>
                        <div style={{ fontWeight: 800, fontSize: '1.1rem' }}><i className="bi bi-envelope-paper me-2"></i>Enviar recordatorio de control</div>
                        <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>{patient.first_name} {patient.last_name} · C.I. {patient.cedula || 'S/C'}</div>
                    </div>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '1.6rem', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
                </div>

                <div className="mr-body">
                    <div>
                        <div className="mr-field">
                            <label>Correo del paciente</label>
                            <input type="email" value={email} onChange={e => { setEmail(e.target.value); setErrores(p => ({ ...p, email: undefined })); }}
                                placeholder="paciente@gmail.com" className={errores.email ? 'mr-err' : ''} />
                            {errores.email && <div className="mr-msg-err">* {errores.email}</div>}
                            {!patient.email && !errores.email && <div className="mr-hint">El paciente no tenía correo: se guardará en su ficha al enviar.</div>}
                        </div>

                        <div className="mr-field">
                            <label>Fecha del control</label>
                            <input type="date" value={fechaControl} onChange={e => setFechaControl(e.target.value)} className={errores.fecha_control ? 'mr-err' : ''} />
                            {errores.fecha_control && <div className="mr-msg-err">* {errores.fecha_control}</div>}
                            <div className="mr-chips">
                                <button type="button" onClick={() => setFechaControl(hoyISO())}>Hoy</button>
                                {[7, 15, 30].map(d => (
                                    <button key={d} type="button" onClick={() => {
                                        const x = new Date(); x.setDate(x.getDate() + d);
                                        setFechaControl(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`);
                                    }}>En {d}d</button>
                                ))}
                                <button type="button" onClick={() => {
                                    const x = new Date(); x.setMonth(x.getMonth() + 6);
                                    setFechaControl(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`);
                                }}>En 6 meses</button>
                                <button type="button" onClick={() => {
                                    const x = new Date(); x.setFullYear(x.getFullYear() + 1);
                                    setFechaControl(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`);
                                }}>En 1 año</button>
                            </div>
                            <div className="mr-hint">
                                {patient.next_checkup_date
                                    ? `Próximo control registrado: ${fecha(patient.next_checkup_date)}.`
                                    : 'El paciente no tenía fecha de próximo control: se guardará esta.'}
                                {vencido && ' El control ya pasó: el correo le invitará a agendar su cita pendiente.'}
                            </div>
                        </div>

                        <div className="mr-field">
                            <label>Sucursal a indicar en el correo</label>
                            <div className="mr-chips">
                                <button
                                    type="button"
                                    style={{
                                        background: sucursal === 'ambas' ? '#111827' : '#fff',
                                        color: sucursal === 'ambas' ? '#fff' : '#374151',
                                        borderColor: sucursal === 'ambas' ? '#111827' : '#d1d5db'
                                    }}
                                    onClick={() => setSucursal('ambas')}
                                >
                                    Ambas sucursales
                                </button>
                                <button
                                    type="button"
                                    style={{
                                        background: sucursal === 'sur' ? '#111827' : '#fff',
                                        color: sucursal === 'sur' ? '#fff' : '#374151',
                                        borderColor: sucursal === 'sur' ? '#111827' : '#d1d5db'
                                    }}
                                    onClick={() => setSucursal('sur')}
                                >
                                    📍 Sucursal Sur
                                </button>
                                <button
                                    type="button"
                                    style={{
                                        background: sucursal === 'norte' ? '#111827' : '#fff',
                                        color: sucursal === 'norte' ? '#fff' : '#374151',
                                        borderColor: sucursal === 'norte' ? '#111827' : '#d1d5db'
                                    }}
                                    onClick={() => setSucursal('norte')}
                                >
                                    📍 Sucursal Norte
                                </button>
                            </div>
                        </div>

                        <button className="mr-btn" onClick={enviar} disabled={enviando || !email || !fechaControl}>
                            <i className="bi bi-send-fill" style={{ color: '#fff' }}></i>
                            <span style={{ color: '#fff' }}>{enviando ? 'Enviando...' : 'Enviar recordatorio'}</span>
                        </button>

                        {resultado && (
                            <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 8, fontWeight: 600, fontSize: '0.88rem',
                                background: resultado.ok ? '#d1fae5' : '#fee2e2', color: resultado.ok ? '#065f46' : '#991b1b' }}>
                                <i className={`bi ${resultado.ok ? 'bi-check-circle-fill' : 'bi-x-circle-fill'} me-1`}></i>{resultado.texto}
                            </div>
                        )}

                        <div style={{ marginTop: 18 }}>
                            <div style={{ fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.06em', color: '#6b7280', textTransform: 'uppercase', marginBottom: 6 }}>
                                Recordatorios enviados a este paciente
                            </div>
                            {anteriores.length ? (
                                <table className="mr-tabla"><tbody>
                                    {anteriores.slice(0, 8).map((r, i) => (
                                        <tr key={i}>
                                            <td>{new Date(r.enviado_en).toLocaleDateString()}</td>
                                            <td>{r.tipo}</td>
                                            <td style={{ color: r.estado === 'enviado' ? '#065f46' : '#991b1b', fontWeight: 700 }}>{r.estado === 'enviado' ? 'Enviado' : 'Error'}</td>
                                        </tr>
                                    ))}
                                </tbody></table>
                            ) : <div className="mr-hint">Ninguno todavía.</div>}
                        </div>
                    </div>

                    <div>
                        <div style={{ fontSize: '0.85rem', marginBottom: 6 }}><strong>Asunto:</strong> {preview?.asunto || '...'}</div>
                        {preview && <iframe title="Vista previa del correo" className="mr-frame" srcDoc={preview.html} sandbox="" />}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModalRecordatorio;
